import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  inject,
  signal,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { PublicInquiryService } from '../../../core/services/listing/public-inquiry.service';
import { AppMainService } from '../../../core/services/core/app-main.service';
import { ScrollLockService } from '../../../core/services/shared/scroll-lock.service';
import { InquiryCreateDto } from '../../../core/models/inquiry-dto.model';
import { InquiryType } from '../../../core/models/api/app-main.model';
import { AnalyticsService } from '../../../core/services/shared/analytics.service';

type ModalState = 'form' | 'submitting' | 'success' | 'error';

@Component({
  selector: 'app-inquiry-modal',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './inquiry-modal.component.html',
  styleUrls: ['./inquiry-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InquiryModalComponent implements OnInit, OnDestroy {
  @Input() productId!: number;
  @Input() inquiryType: 'VIEWING' | 'INTEREST' = 'INTEREST';
  @Input() brokerName: string = 'our team';
  @Output() close = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private inquiryService = inject(PublicInquiryService);
  private appMainService = inject(AppMainService);
  private scrollLock = inject(ScrollLockService);
  private analytics = inject(AnalyticsService);

  modalState = signal<ModalState>('form');
  inquiryTypes = signal<InquiryType[]>([]);

  form = this.fb.nonNullable.group({
    inquiryTypeId: [<number | null>null, Validators.required],
    firstName: ['', [Validators.required, Validators.minLength(2)]],
    lastName: ['', [Validators.required, Validators.minLength(2)]],
    email: ['', [Validators.required, Validators.email]],
    phone: [
      '',
      [
        Validators.required,
        Validators.pattern(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/),
      ],
    ],
    message: ['', [Validators.required, Validators.minLength(10)]],
  });

  ngOnInit(): void {
    this.scrollLock.lock();

    // Fetch Inquiry Types from backend
    this.appMainService.getMasterConfig().subscribe((config) => {
      if (config?.inquiryTypes) {
        this.inquiryTypes.set(config.inquiryTypes);

        // Pre-select a default based on the button clicked if possible
        const defaultTypeName =
          this.inquiryType === 'VIEWING' ? 'Schedule Appointment' : 'Send Message';
        const defaultType = config.inquiryTypes.find((t) => t.inquiryTypeName === defaultTypeName);
        if (defaultType) {
          this.form.controls.inquiryTypeId.setValue(defaultType.inquiryTypeId);
        } else if (config.inquiryTypes.length > 0) {
          this.form.controls.inquiryTypeId.setValue(config.inquiryTypes[0].inquiryTypeId);
        }
      }
    });

    // Pre-fill message hint by inquiry type
    const hint = this.inquiryType === 'VIEWING' ? '' : '';
    this.form.controls.message.setValue(hint);
  }

  ngOnDestroy(): void {
    this.scrollLock.unlock();
  }

  @HostListener('document:keydown.escape')
  onEscKey(): void {
    if (this.modalState() === 'form' || this.modalState() === 'error') {
      this.onClose();
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      if (this.modalState() === 'form' || this.modalState() === 'error') {
        this.onClose();
      }
    }
  }

  get firstNameCtrl(): AbstractControl {
    return this.form.controls.firstName;
  }
  get lastNameCtrl(): AbstractControl {
    return this.form.controls.lastName;
  }
  get emailCtrl(): AbstractControl {
    return this.form.controls.email;
  }
  get messageCtrl(): AbstractControl {
    return this.form.controls.message;
  }
  get inquiryTypeCtrl(): AbstractControl {
    return this.form.controls.inquiryTypeId;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.modalState.set('submitting');
    const raw = this.form.getRawValue();

    // Look up the NAME of the selected inquiry type — backend uses string `inquiry_type` column
    const selectedType = this.inquiryTypes().find((t) => t.inquiryTypeId === raw.inquiryTypeId);
    const inquiryTypeName = selectedType?.inquiryTypeName || 'General';

    const rawMessage = raw.message || '';
    const words = rawMessage.trim().split(/\s+/).slice(0, 6);
    const subject = words.join(' ') + (words.length >= 6 ? '...' : '');

    const dto: InquiryCreateDto = {
      productId: this.productId,
      name: `${raw.firstName} ${raw.lastName}`.trim(),
      firstName: raw.firstName,
      lastName: raw.lastName,
      email: raw.email,
      phone: raw.phone || undefined,
      message: rawMessage,
      subject: subject,
      inquiryTypeId: raw.inquiryTypeId ?? undefined,
      inquiryType: inquiryTypeName,
    };

    this.inquiryService.submitInquiry(dto).subscribe({
      next: () => {
        this.modalState.set('success');
        this.analytics.trackLead('listing_inquiry', this.productId);
        setTimeout(() => this.onClose(), 2500);
      },
      error: () => {
        this.modalState.set('error');
      },
    });
  }

  onClose(): void {
    this.close.emit();
  }

  get modalTitle(): string {
    return this.inquiryType === 'VIEWING' ? 'Request a Viewing' : 'Express Interest';
  }
}
